import * as React from "react";
import {Alert, Button, Checkbox, Col, ControlLabel, Form, FormControl, FormGroup, Row} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Warning} from "../common/model";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {CombinedCronPicker, Mode} from "../common/ui/cron/CombinedCronPicker";
import {CronUtil, CronValidationResult} from "../common/ui/cron/CronUtil";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AOIDialog} from "./AOIDialog";
import {AOIEventsDialog} from "./aoiEvents/AOIEventsDialog";
import {AOIList} from "./AOIList";
import {DeleteAOIButton} from "./DeleteAOIButton";
import {AOINotificationArea, AOIMonitor} from "./model";

const MESSAGES = defineMessages({
    usernameRequired: {
        id: "studio.aoi.username.validation.message",
        defaultMessage: "User Name is required",
    },
    emailRequired: {
        id: "studio.aoi.email.validation.message",
        defaultMessage: "Email address is required",
    },
    validEmailRequired: {
        id: "studio.aoi.valid-email.validation.message",
        defaultMessage: "Email address is not valid",
    }
});


interface AOIUserFormOwnProps {
    onClose: () => void;
    onSubmit: () => void;
    refreshData: () => Promise<any>;
    userId: string;
    notification: AOIMonitor;
    browseAOIs: boolean;
}

interface AOIUserFormState {
    error: Error;
    executing: boolean;
    isAOIDialogOpen: boolean;
    isAOIEventsDialogOpen: boolean;
    username: string,
    email: string;
    enabled: boolean;
    schedule: string;
    notificationAreaIndex: number;
    triggers: AOINotificationArea[];
    cronPickerMode: Mode,
}

type AOIUserFormProps = AOIUserFormOwnProps & WithApiProperties & InjectedIntlProps;

const LABEL_COL = 4;
const CONTROL_COL = 8;

class AOIUserFormComponent extends React.Component<AOIUserFormProps, AOIUserFormState> {

    constructor(props: AOIUserFormProps) {
        super(props);

        const notification = props.notification;
        const cronString = notification?.schedule || "0 0 0 * * *";
        let mode: Mode = "simple";
        if (cronString) {
            mode = CombinedCronPicker.canUseSimpleMode(cronString, this.props.intl) ? "simple" : "advanced";
        }

        this.state = {
            executing: false,
            error: null,
            isAOIDialogOpen: false,
            isAOIEventsDialogOpen: false,
            username: notification?.username || "",
            email: notification?.email || "",
            enabled: notification ? notification.enabled : true,
            schedule: cronString,
            notificationAreaIndex: null,
            triggers: [],
            cronPickerMode: mode,
        };
    }

    handleRowSelect = (selection: AOINotificationArea[]) => {
        this.setState({triggers: selection});
    }

    clearSelection = () => {
        this.handleRowSelect([]);
    }

    validateSchedule = (cronStringToValidate: string): Promise<CronValidationResult> => {
        const validate = (validateCRON) => Promise.resolve(validateCRON);
        return validate(CronUtil.validateCronString(cronStringToValidate, this.props.intl));
    }

    switchMode = (newMode) => {
        if (newMode === "notset") {
            this.setState({schedule: undefined, cronPickerMode: newMode});
        } else if (newMode === "simple" &&
                   (!CombinedCronPicker.canUseSimpleMode(this.state.schedule, this.props.intl))) {
            this.setState(prevState =>
                ({schedule: prevState.schedule || "0 0 0 * * *", cronPickerMode: newMode})
            );
        } else if (newMode === "advanced" && !this.state.schedule) {
            this.setState({schedule: "0 0 0 * * *", cronPickerMode: newMode});
        } else {
            this.setState({cronPickerMode: newMode});
        }
    }

    render = () => {
        const {browseAOIs, userId, notification, onClose} = this.props;
        const {isAOIDialogOpen, isAOIEventsDialogOpen, triggers, notificationAreaIndex} = this.state;
        const noSelection = triggers.length === 0;
        const notificationAreaId = notificationAreaIndex != null && notification.triggers[notificationAreaIndex].id;

        return (
            <Form horizontal>
                {!browseAOIs && <React.Fragment>
                    {this.renderContent()}

                    {!this.state.executing && <FormGroup>
                        <Col smOffset={1} sm={10}>
                            <ActionBar>
                                <Button onClick={this.submit} disabled={!!this.state.error}>
                                    {!notification && <FormattedMessage id="studio.form.create"
                                                                        defaultMessage="Create"/>}
                                    {notification && <FormattedMessage id="studio.form.save"
                                                                       defaultMessage="Save"/>}
                                </Button>
                                <Button onClick={onClose}>
                                    <FormattedMessage id="studio.form.cancel"
                                                      defaultMessage="Cancel"/>
                                </Button>
                            </ActionBar>
                        </Col>
                    </FormGroup>}
                </React.Fragment>}

                {browseAOIs && <React.Fragment>
                    <div>
                        <ActionBar>
                            <div>
                                <Button onClick={this.openAOIDialog}>
                                    <LcdIcon icon={"plus"}/>
                                    <FormattedMessage id="studio.notifications.create-aoi"
                                                      defaultMessage="Create AOI"/>
                                </Button>
                            </div>
                            <div>
                                <DeleteAOIButton disabled={noSelection} data={triggers}
                                                 onDeleteSuccess={this.onDeleteAOISuccess}
                                                 onDelete={this.deleteAOI}
                                                 onDeleteWarnings={this.clearSelection}/>
                            </div>
                        </ActionBar>
                        <AOIList className="public_fixedDataTable_on_drawer" userId={userId}
                                 onRowClick={this.onAOIClick} onRowSelect={this.handleRowSelect}
                                 clearSelection={noSelection}/>
                        {isAOIDialogOpen &&
                         <AOIDialog api={this.props.api} notification={notification}
                                    notificationAreaIndex={notificationAreaIndex}
                                       onSuccess={this.onAOISaved} onHide={this.closeAOIDialog}/>}
                        {isAOIEventsDialogOpen &&
                            <AOIEventsDialog notificationId={notification.id} notificationAreaId={notificationAreaId}
                                             onHide={this.closeAOIEventsDialog}/>}
                    </div>
                    <FormGroup>
                        <Col smOffset={1} sm={10}>
                            <ActionBar>
                                <Button onClick={onClose}>
                                    <FormattedMessage id="studio.ui.form-buttons.close"
                                                      defaultMessage="Close"/>
                                </Button>
                            </ActionBar>
                        </Col>
                    </FormGroup>
                </React.Fragment>}
            </Form>
        );
    }

    onDeleteAOISuccess = () => {
        this.clearSelection();
        this.props.refreshData();
    };

    renderContent = () => {
        if (this.state.executing) {
            return this.renderExecuting();
        }

        return (
            <React.Fragment>
                {this.state.error && this.renderError()}
                <FormGroup controlId="username">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.name"
                                          defaultMessage="User Name"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="username" value={this.state.username} onChange={this.onChange}
                                     disabled={!!this.props.notification}/>
                    </Col>
                </FormGroup>

                <FormGroup controlId="email">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.email"
                                          defaultMessage="Email"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="email" value={this.state.email} onChange={this.onChange}/>
                    </Col>
                </FormGroup>

                <FormGroup controlId="schedule">
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.notifications.aoi-user-form.schedule"
                                          defaultMessage="Schedule"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <CombinedCronPicker value={this.state.schedule}
                                            intl={this.props.intl}
                                            disableNotset={true}
                                            disableMinutes={true}
                                            validate={this.validateSchedule}
                                            mode={this.state.cronPickerMode}
                                            onModeSwitch={this.switchMode}
                                            onChange={(cronString) => {
                                                this.setState({schedule: cronString ?? undefined});
                                            }}/>
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col smOffset={LABEL_COL} sm={CONTROL_COL}>
                        <Checkbox name="enabled" checked={this.state.enabled} onChange={this.onChange}>
                            <FormattedMessage id="studio.notifications.aoi-user-form.enable-notifications"
                                              defaultMessage="Enable notifications"/>
                        </Checkbox>
                    </Col>
                </FormGroup>
            </React.Fragment>
        );
    }

    onChange = (event: any) => {
        const {name} = event.target;
        let {value} = event.target;
        value = event.target.type === 'checkbox' ? event.target.checked : value;
        this.setState({[name]: value, error: null} as any);
    };

    onAOIClick = (event, notificationAreaIndex) => {
        if (event.target.dataset.isMainLink) {
            this.setState({isAOIDialogOpen: true, notificationAreaIndex});

            return true;
        }

        if (event.target.dataset.isBrowseEventsLink) {
            this.setState({isAOIEventsDialogOpen: true, notificationAreaIndex});

            return true;
        }
    };

    openAOIDialog = () => {
        this.setState({isAOIDialogOpen: true});
    };

    closeAOIDialog = () => {
        this.setState({isAOIDialogOpen: false, notificationAreaIndex: null});
    };

    closeAOIEventsDialog = () => {
        this.setState({isAOIEventsDialogOpen: false, notificationAreaIndex: null});
    };

    onAOISaved = () => {
        this.props.refreshData()
            .then(() => {
                this.closeAOIDialog();
            });
    };

    renderExecuting = () => {
        const {executing} = this.state;
        if (executing) {
            return (
                <Row style={{textAlign: "center"}}>
                    <h2>
                        <FormattedMessage id="studio.notifications.create-aoi-user-form.saving-aoi-user"
                                          defaultMessage="Saving AOI user..."/>
                    </h2>
                    <Spinner bigStyle/>
                </Row>
            );
        }
        return null;
    }

    renderError = () => {
        const {error} = this.state;
        const errorMessage = mapErrorToMessage(error);
        if (errorMessage) {
            return <Alert bsStyle="danger">{errorMessage}</Alert>;
        }
        return <ErrorDisplay error={error}/>;
    }

    submit = () => {
        const {api, notification, onSubmit, intl} = this.props;

        if (!this.state.username) {
            this.setState({error: new Error(intl.formatMessage(MESSAGES.usernameRequired))});
            return;
        }
        if (!this.state.email) {
            this.setState({error: new Error(intl.formatMessage(MESSAGES.emailRequired))});
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.state.email)) {
            this.setState({error: new Error(intl.formatMessage(MESSAGES.validEmailRequired))});
            return;
        }

        Promise.resolve()
            .then(() => {

                this.setState({executing: true});

                const monitorToSave = {
                    id: this.props.notification ? this.props.notification.id : null,
                    email: this.state.email,
                    username: this.state.username,
                    schedule: this.state.schedule,
                    enabled: this.state.enabled,
                    triggers: this.props.notification ? this.props.notification.triggers : [],
                };
                if (monitorToSave.id == null) {
                    delete monitorToSave["id"];
                }

                if (this.props.notification) {
                    return api.updateAOINotification({...notification, ...monitorToSave});
                } else {
                    return api.createAOINotification({
                        ...monitorToSave,
                    });
                }
            })
            .catch((error) => {
                this.setState({executing: false, error});

                throw error;
            })
            .then(() => {
                this.setState({executing: false});

                onSubmit();
            });
    };

    deleteAOI = (): Promise<Warning[]> => {
        const {api, notification} = this.props;
        const {triggers} = this.state;

        return api.deleteAOINotificationArea(notification, triggers[0])
            .then(_r => null);
    };

}

export const AOIUserForm = WithApi(injectIntl(AOIUserFormComponent));